import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller from '@ember/controller';
import { isEmpty, isNone } from '@ember/utils';
import { Entities, Periods } from 'cio/utils/metrics/common';
import AlertsService from 'cio/services/alerts';
import EnvironmentService from 'cio/services/environment';
import ExportsService from 'cio/services/exports';
import MetricsService from 'cio/services/metrics';
import NewsletterModel from 'cio/models/newsletter';
import { hash, task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { Metric } from 'cio/utils/metrics/models/metric';
import { ActionType } from 'cio/models/action';

export default class NewsletterOverviewReportsIndexController extends Controller {
  declare model: NewsletterModel;

  queryParams = ['templateId'];

  @service alerts!: AlertsService;
  @service environment!: EnvironmentService;
  @service exports!: ExportsService;
  @service('metrics') metricsService!: MetricsService;

  templateId = '';

  @tracked newsletterMetric: Metric | null = null;
  @tracked templateMetrics: Metric[] | null = null;
  @tracked isMessagingLimitEnabled = false;

  @computed('model.activeTemplates.@each.{id,name}')
  get metricItems() {
    return [{ value: '', label: 'All variations' }, ...this.model.activeTemplatesSelectItems];
  }

  @computed('model', 'templateId', 'newsletterMetric', 'templateMetrics.[]')
  get selectedMetric() {
    if (isEmpty(this.templateId)) {
      return this.newsletterMetric;
    }

    let template = this.model.activeTemplates.findBy('id', this.templateId);

    if (isNone(template)) {
      return this.newsletterMetric;
    }

    return (this.templateMetrics ?? []).find((metric) => metric.entityId === this.templateId);
  }

  @computed('model', 'templateId')
  get trackedLinkMetricQueryParams() {
    if (isEmpty(this.templateId)) {
      return {
        type: Entities.NEWSLETTER,
        newsletter_id: this.model.id,
      };
    }

    return {
      type: Entities.TEMPLATE,
      template_id: this.templateId,
    };
  }

  fetchMetrics = task({ keepLatest: true }, async () => {
    let actionType = `${this.model.channel}_action` as ActionType;
    let results = await hash({
      newsletterMetric: this.metricsService.fetchMetric(
        Entities.NEWSLETTER,
        this.model.id,
        Periods.DAYS,
        null,
        actionType,
        {
          environmentId: this.environment.current.id,
          newsletterId: this.model.id,
          // all newsletters can convert regardless of channel
          canConvert: true,
        },
      ),
      templateMetrics: this.metricsService.fetchTemplateMetrics(this.model.id, actionType),
    });

    this.newsletterMetric = results.newsletterMetric;
    this.templateMetrics = results.templateMetrics;
  });

  @action
  createDeliveryTotalsExport() {
    let options: Record<string, string> = { newsletterId: this.model.id, from: 'newsletter_overview' };

    if (this.templateId !== '') {
      options.templateId = this.templateId;
    }

    return this.exports.deliveryTotals(options);
  }
}

declare module '@ember/controller' {
  interface Registry {
    'broadcasts.newsletter.overview.reports.index': NewsletterOverviewReportsIndexController;
  }
}
